import React, { useCallback, useEffect, useRef, useState } from 'react';
import Icon from '../shared/Icon';
import { CardGrid } from '../shared/Card';
import { plural } from '../../../models/LibraryModel';
import { uploadsStarted } from '../../../models/AssessmentsModel';
import { useAssessmentsViewModel } from '../../../viewModels/useAssessmentsViewModel';
import ArchiveConfirmDialog from '../Library/ArchiveConfirmDialog';
import AssessmentsToolbar from './AssessmentsToolbar';
import AssessmentCard from './AssessmentCard';
import AssessmentsList from './AssessmentsList';
import AddAssessmentDialog from './AddAssessmentDialog';
import AssessmentDetail from '../AssessmentDetail/AssessmentDetail';
import { useAssessmentActions } from './useAssessmentActions';
import '../Library/Library.css'; // the Library's toolbar, cards, count line and empty state
import './Assessments.css';

/**
 * Data Sources > Assessments (PRD-1703, Navy Phase 0 handoff "Assessments").
 * Grid by default, or the List table; search, scenario, status and visibility
 * filters, and a sort in the grid. With no assessments at all the panel holds
 * the empty state and there is no count line. Archive asks first; Unarchive
 * doesn't. Add assessment lives in the Data Sources header, so its dialog is
 * opened from there (`addOpener`) or from the empty state. A card or row opens
 * the assessment's detail, which reads the record live from the model, so it
 * follows the record as it moves on, and closes once it is archived.
 */
function Assessments({ model, addOpener, onOpenAdd, onCloseAdd, headerAddRef, onShowToast, onManageScenarios }) {
  const vm = useAssessmentsViewModel(model);
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const actionsHook = useAssessmentActions({ vm, rootRef, fallbackRef: headerAddRef, onShowToast });
  const { filters, visibleAssessments, scopeTotal, total, scenarioName } = vm;
  const count = visibleAssessments.length;
  const closeAdd = useCallback(() => onCloseAdd(), [onCloseAdd]);

  const [detail, setDetail] = useState(null); // { id, opener, archived: as last seen }
  const openDetail = useCallback((a, opener) => setDetail({ id: a.id, opener, archived: !!a.archived }), []);
  const closeDetail = useCallback(() => setDetail(null), []);
  const detailRecord = detail && vm.assessments.find((a) => a.id === detail.id);
  const detailScenario = detailRecord && vm.scenarios.find((s) => s.id === detailRecord.scenarioId);
  // Archived while open (from the detail or not): the dialog goes with it. An
  // archived one opened from the Archived filter stays open, and once
  // unarchived there, archiving it again closes it too.
  const archivedUnderneath = !!detail && (!detailRecord || (detailRecord.archived && !detail.archived));
  const unarchivedUnderneath = !!detail && !!detailRecord && !detailRecord.archived && detail.archived;
  useEffect(() => {
    if (archivedUnderneath) setDetail(null);
    else if (unarchivedUnderneath) setDetail((d) => ({ ...d, archived: false }));
  }, [archivedUnderneath, unarchivedUnderneath]);

  const handleClear = () => {
    vm.clearSearchAndFilters();
    searchRef.current?.focus();
  };

  const handleSave = (fields) => {
    const created = vm.addAssessments(fields);
    if (!created.length) return;
    onCloseAdd();
    onShowToast?.(uploadsStarted(created.length), 'success');
  };

  let body = null;
  if (!total) {
    body = (
      <div className="library-empty">
        <Icon name="waveform" size={32} className="library-empty-icon" />
        <h2 className="library-empty-title">No assessments yet</h2>
        <p className="library-empty-note">Upload a recording or a transcript to create one.</p>
        <button type="button" className="resui-btn resui-btn--primary" onClick={(e) => onOpenAdd(e.currentTarget)}>Add assessment</button>
      </div>
    );
  } else if (count && vm.view === 'grid') {
    body = (
      <CardGrid className="library-grid">
        {visibleAssessments.map((a) => (
          <AssessmentCard key={a.id} assessment={a} scenarioName={scenarioName(a.scenarioId)} actions={actionsHook.actions} onOpen={openDetail} />
        ))}
      </CardGrid>
    );
  } else if (count) {
    body = (
      <AssessmentsList
        assessments={visibleAssessments}
        scenarioName={scenarioName}
        actions={actionsHook.actions}
        onOpen={openDetail}
        sort={vm.listSort}
        onSortChange={vm.setListSort}
        resetKey={`${filters.scenario}|${filters.status}|${filters.search}|${filters.visibility}|${total}`}
        focusId={actionsHook.focusId}
      />
    );
  }

  return (
    <div className="assessments" ref={rootRef}>
      <AssessmentsToolbar
        filters={filters}
        view={vm.view}
        scenarios={vm.scenarios}
        onFilterChange={vm.setFilter}
        onViewChange={vm.setView}
        searchRef={searchRef}
      />

      {total > 0 && (
        <p className="library-count-line" aria-live="polite">
          {count ? (
            `${count} of ${scopeTotal} ${plural(scopeTotal, 'assessment', 'assessments')}${filters.search.trim() ? ' found' : ''}`
          ) : (
            <>
              No assessments found.{' '}
              <button type="button" className="resui-link" onClick={handleClear}>Clear search and filters</button>
            </>
          )}
        </p>
      )}

      {body}

      {addOpener && (
        <AddAssessmentDialog
          vm={vm}
          opener={addOpener}
          fallbackFocus={headerAddRef}
          onSave={handleSave}
          onClose={closeAdd}
          onManageScenarios={onManageScenarios}
        />
      )}

      {detailRecord && !archivedUnderneath && (
        <AssessmentDetail
          assessment={detailRecord}
          scenario={detailScenario}
          documents={vm.documents}
          opener={detail.opener}
          fallbackFocus={headerAddRef}
          covered={!!actionsHook.confirming}
          onClose={closeDetail}
          {...actionsHook.detailActions}
        />
      )}

      {actionsHook.confirming && (
        <ArchiveConfirmDialog
          item={actionsHook.confirming.assessment}
          kind="assessment"
          opener={actionsHook.confirming.opener}
          fallbackFocus={headerAddRef}
          onConfirm={actionsHook.confirmArchive}
          onClose={actionsHook.cancelArchive}
        />
      )}
    </div>
  );
}

export default Assessments;
